import { sampleListings } from "@/data/sample-listings";

const buildSpinFrameUrls = (basePath: string, count: number, extension: "webp" | "jpg" | "png" | "jpeg" | "svg" = "webp") =>
  Array.from({ length: count }, (_, index) => {
    const frameNumber = String(index + 1).padStart(3, "0");
    return `${basePath}/frame-${frameNumber}.${extension}`;
  });

const buildSpinFrameCandidates = (
  basePath: string,
  count: number,
  extensions: Array<"webp" | "jpg" | "png" | "jpeg" | "svg">
) =>
  Array.from({ length: count }, (_, index) => {
    const frameNumber = String(index + 1).padStart(3, "0");
    return extensions.map((extension) => `${basePath}/frame-${frameNumber}.${extension}`);
  });

export interface ProductSpinItem {
  id: string;
  title: string;
  category: string;
  description: string;
  captureNotes: string;
  posterImage: string;
  spinFrameUrls?: string[];
  spinFrameCandidates?: string[][];
  accent: string;
  frameCount: number;
  placeholderMode?: "simulated";
}

export interface ShowroomExperienceItem {
  id: string;
  title: string;
  location: string;
  description: string;
  imageUrl: string;
  tourUrl: string;
}

const showroomSlugs = [
  "asokoro-estate-7-5b-capture",
  "amsterdam-bar-grills",
  "asokoro-9br-ultra-luxury-residence",
] as const;

const showroomListings = showroomSlugs
  .map((slug) => sampleListings.find((listing) => listing.slug === slug))
  .filter((listing): listing is NonNullable<(typeof sampleListings)[number]> => Boolean(listing));

export const productSpinItems: ProductSpinItem[] = [
  {
    id: "pilot-watch",
    title: "Pilot Watch Demo",
    category: "Luxury Accessories",
    description: "A 24-frame watch spin demo for product inspection, ad creative, and storefront conversion.",
    captureNotes: "Drop real frame images into public/spins/pilot-watch and the demo will prefer them before the SVG fallback sequence.",
    posterImage: "/spins/pilot-watch/frame-001.svg",
    spinFrameCandidates: buildSpinFrameCandidates("/spins/pilot-watch", 24, ["webp", "png", "jpg", "jpeg", "svg"]),
    accent: "from-amber-300/30 via-orange-400/10 to-transparent",
    frameCount: 24,
  },
  {
    id: "pilot-chair",
    title: "Studio Chair Demo",
    category: "Furniture",
    description: "A furniture-ready product spin concept for showrooms, catalog sales, and social commerce previews.",
    captureNotes: "Simulated 36-frame studio spin with directional lighting cues.",
    posterImage: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80",
    spinFrameUrls: buildSpinFrameUrls("/spins/pilot-chair", 36),
    accent: "from-sky-300/25 via-cyan-400/10 to-transparent",
    frameCount: 36,
    placeholderMode: "simulated",
  },
  {
    id: "pilot-sneaker",
    title: "Retail Sneaker Demo",
    category: "Fashion Retail",
    description: "A commerce-focused spin experience designed for launches, ad campaigns, and direct WhatsApp selling.",
    captureNotes: "Simulated 24-frame catalog spin prepared for mobile-first storefronts.",
    posterImage: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1200&q=80",
    spinFrameUrls: buildSpinFrameUrls("/spins/pilot-sneaker", 24),
    accent: "from-rose-300/30 via-red-400/10 to-transparent",
    frameCount: 24,
    placeholderMode: "simulated",
  },
];

export const showroomExperienceItems: ShowroomExperienceItem[] = showroomListings.map((listing) => ({
  id: listing.id,
  title: listing.title,
  location: listing.location,
  description: listing.description,
  imageUrl: listing.imageUrl,
  tourUrl: listing.tourUrl,
}));
